import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './Login.css';

const Login: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Check for URL parameters and auto-login
  useEffect(() => {
    const urlUsername = searchParams.get('username');
    const urlPassword = searchParams.get('password');

    if (urlUsername && urlPassword && !isAuthenticated) {
      // Auto-login with URL parameters
      setUsername(urlUsername);
      setPassword(urlPassword);
      
      const handleAutoLogin = async () => {
        setLoading(true);
        setError('');

        try {
          const success = await login(urlUsername, urlPassword);
          if (success) {
            // Clear URL parameters for security
            setSearchParams({});
            navigate('/');
          } else {
            setError('Invalid username or password from URL parameters');
            // Clear URL parameters even on failure
            setSearchParams({});
          }
        } catch (err) {
          setError('An error occurred during login');
          setSearchParams({});
        } finally {
          setLoading(false);
        }
      };

      handleAutoLogin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, isAuthenticated, login, navigate, setSearchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const success = await login(username, password);
      if (success) {
        navigate('/');
      } else {
        setError('Invalid username or password');
      }
    } catch (err) {
      setError('An error occurred during login');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <h1 className="login-title">Email Viewer</h1>
        <p className="login-subtitle">Sign in to view your emails</p>
        <form onSubmit={handleSubmit} className="login-form">
          {error && <div className="error-message">{error}</div>}
          <div className="form-group">
            <label htmlFor="username">Username</label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoComplete="username"
              placeholder="Enter your username"
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              placeholder="Enter your password"
            />
          </div>
          <button type="submit" className="login-button" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;

